'use client'

import { Loader } from "@googlemaps/js-api-loader"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { useEffect, useRef, useState } from "react"
import dynamic from 'next/dynamic'
import imgMarker from '@/assets/img/map/marker.png'
import imgMarker1 from '@/assets/img/map/marker-1.png'
import imgMarker2 from '@/assets/img/map/marker-2.png'
import imgMarker3 from '@/assets/img/map/marker-3.png'
import imgMarker4 from '@/assets/img/map/marker-4.png'
import imgMarker5 from '@/assets/img/map/marker-5.png'
import imgMarker6 from '@/assets/img/map/marker-6.png'
import imgMarker7 from '@/assets/img/map/marker-7.png'
import imgMarker8 from '@/assets/img/map/marker-8.png'
import imgMarker9 from '@/assets/img/map/marker-9.png'
import imgMarker10 from '@/assets/img/map/marker-10.png'
import { useRouter } from "next/navigation"

type Point = {
    latitude: number,
    longitude: number
}

type Marker = {
    position: Point,
    title: string,
    type: string,
    infowindow?: {
        content: string
        target?: string,
        fetchContent?: Function
    },
    object?: Searchable
}


type MapProps = {
    center?: Point,
    zoom?: number,
    markers?: Marker[]
    boundaries?: {
        north: number,
        east: number,
        south: number,
        west: number
    },
    clustering?: boolean,
    setMap?: Function,
    setMappedMarkers?: Function,
    setActiveObject?: Function
}

export default function Map({
    center,
    zoom,
    markers = [],
    boundaries,
    clustering = false,
    setMap: setParentMap = (map: google.maps.Map) => { },
    setMappedMarkers = (markers: any[]) => { },
    setActiveObject = (object: Searchable) => { }
}: MapProps) {

    const router = useRouter()

    const loader = new Loader({
        apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
        version: "weekly",
    });

    const ref = useRef<HTMLDivElement>(null)
    const [map, setMap] = useState<google.maps.Map>(null)
    const refMarkers = useRef<google.maps.Marker[]>([])
    const refInfowindows = useRef<google.maps.InfoWindow[]>([])
    const refInfowindow = useRef<google.maps.InfoWindow>()
    const refMarkerClusterer = useRef<MarkerClusterer>(null)
    const [oms, setOms] = useState(null)
    const refTarget = useRef<string>(null)

    const defaultZoom = 12
    const defaultMaxZoom = 18

    async function initMarkers(markers) {

        refMarkers.current.map((marker) => {
            marker.setMap(null)
        })
        refMarkers.current = []
        if (refMarkerClusterer.current !== null) {
            refMarkerClusterer.current.clearMarkers()
            refMarkerClusterer.current.setMap(null)
            refMarkerClusterer.current = null
        }
        refInfowindow.current?.close()

        let mappedMarkers = []

        const numberedIcons = [
            imgMarker1,
            imgMarker2,
            imgMarker3,
            imgMarker4,
            imgMarker5,
            imgMarker6,
            imgMarker7,
            imgMarker8,
            imgMarker9,
            imgMarker10
        ]

        markers.forEach((marker: Marker, indexMarker: number) => {

            const iconUrl =
                marker.type === 'number'
                    ? numberedIcons[indexMarker]?.src
                    : imgMarker.src

            const gmMarker = new google.maps.Marker({
                position: {
                    lat: marker.position.latitude,
                    lng: marker.position.longitude
                },
                map,
                title: marker.title,
                icon: {
                    url: iconUrl,
                    scaledSize: new google.maps.Size(34, 40),
                    anchor: new google.maps.Point(16, 40) // centre bas
                }
            })

            if (marker.infowindow) {
                gmMarker.addListener('click', async () => {

                    console.log('click IW')

                    refInfowindow.current.close()

                    refInfowindow.current.setContent(marker.infowindow.content)
                    refInfowindow.current.setOptions({
                        pixelOffset: new google.maps.Size(0, -40)
                    })

                    refInfowindow.current.setPosition(gmMarker.getPosition())
                    refInfowindow.current.open(map)

                    refTarget.current = marker.infowindow.target
                    setActiveObject(marker.object)

                    if (marker.infowindow.fetchContent) {
                        const content = await marker.infowindow.fetchContent()
                        refInfowindow.current.setContent(content)
                    }
                })
            }

            mappedMarkers.push({ marker, gmMarker })
            refMarkers.current.push(gmMarker)

            oms?.addMarker(gmMarker)
        })

        // ✅ clustering inchangé
        if (clustering) {
            refMarkerClusterer.current = new MarkerClusterer({
                map,
                markers: refMarkers.current,
                algorithmOptions: {
                    maxZoom: 15
                },
                renderer: {
                    render({ count, position }, stats) {
                        const color =
                            count > stats.clusters.markers.mean
                                ? '#1b706a'
                                : '#5f9c99'

                        const svg = window.btoa(`
            <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
              <circle cx="120" cy="120" opacity=".8" r="70" />
            </svg>
          `)

                        return new google.maps.Marker({
                            position,
                            icon: {
                                url: `data:image/svg+xml;base64,${svg}`,
                                scaledSize: new google.maps.Size(75, 75),
                            },
                            label: {
                                text: String(count),
                                color: "#fff",
                                fontSize: "16px",
                                fontWeight: "700"
                            },
                            zIndex: google.maps.Marker.MAX_ZINDEX + count
                        })
                    }
                }
            })
        }

        setMappedMarkers(mappedMarkers)
    }

    useEffect(() => {

        async function load() {
            const { Map, InfoWindow } = await loader.importLibrary('maps')
            const { event } = await loader.importLibrary('core')

            const googleMap = new Map(ref.current.parentElement, {
                ...center && { center: { lat: center.latitude, lng: center.longitude } },
                zoom: zoom ?? defaultZoom,
                maxZoom: 18,
                mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
                mapTypeControl: false,
                zoomControl: false,
                scaleControl: false,
                streetViewControl: false,
                fullscreenControl: false
            })

            refInfowindow.current = new InfoWindow()
            refInfowindow.current.addListener('domready', (e) => {
                googleMap.getDiv().querySelector('.gclose')?.addEventListener('click', (e) => {
                    setActiveObject(null)
                    refInfowindow.current.close()
                    e.stopPropagation()
                })
                googleMap.getDiv().querySelector('.vignette.map-info')?.addEventListener('click', (e) => {
                    router.push(refTarget.current)
                })
            })

            const zoomOutControlDiv = document.createElement("div");
            // Create the control.
            const zoomOutControl = document.createElement("button");
            // Set CSS for the control.
            zoomOutControl.style.cursor = "pointer";
            zoomOutControl.innerHTML = "<i class='fi fi-bs-minus'></i>";
            zoomOutControl.title = "Zoom arrière";
            zoomOutControl.type = "button";
            zoomOutControl.className = "zoomout";
            zoomOutControl.addEventListener("click", () => {
                googleMap.setZoom(googleMap.getZoom() - 1);
            });
            // Append the control to the DIV.
            zoomOutControlDiv.appendChild(zoomOutControl);

            googleMap.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
                zoomOutControlDiv
            );

            // Create the DIV to hold the control.
            const zoomInControlDiv = document.createElement("div");
            // Create the control.
            const zoomInControl = document.createElement("button");
            // Set CSS for the control.
            zoomInControl.style.cursor = "pointer";
            zoomInControl.innerHTML = "<i class='fi fi-bs-plus'></i>";
            zoomInControl.title = "Zoom avant";
            zoomInControl.type = "button";
            zoomInControl.className = "zoomin";
            zoomInControl.addEventListener("click", () => {
                googleMap.setZoom(googleMap.getZoom() + 1);
            });
            // Append the control to the DIV.
            zoomInControlDiv.appendChild(zoomInControl);

            googleMap.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
                zoomInControlDiv
            );

            if (boundaries) {
                googleMap.fitBounds(boundaries)
            }

            setMap(googleMap)
            setParentMap(googleMap)
        }

        load()

    }, [])

    useEffect(() => {

        if (!map || oms) return

        let isMounted = true

        const loadOms = async () => {
            await loader.load()
            const { default: OverlappingMarkerSpiderfier } = await import('overlapping-marker-spiderfier')

            setOms(new OverlappingMarkerSpiderfier(map, {
                keepSpiderfied: true,
                nearbyDistance: 20,
                legWeight: 1,
                legColors: {
                    usual: '#666',
                    highlighted: '#000'
                }
            }))
        }

        loadOms()

        return () => {
            isMounted = false
        }
    }, [map])

    useEffect(() => {

        console.log(map, oms)

        if (!map) return

        initMarkers(markers)

    }, [map, oms, markers])

    return (
        <div ref={ref} />
    )
}